import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import SearchPanel, { ValueChangedEvent } from "@widgets/SearchPanel";
import { callApi, fnGetDataSet } from "@shared/api/restApi";
import { getInitFormData } from "@shared/utils/CommonUtils";
import { showDialog } from "@widgets/CommonDialog";
import { global } from "@shared/libs/Global";
import SnGrid from "@shared/components/GridV2/SnGrid";
import { menuInfo } from "@shared/type/CommonType";
import SnResizable from "@shared/components/SnResizable";
import usePanelSizes from "@shared/hooks/usePanelSizes";

interface Props {
  Menuprops: menuInfo;
}

interface RowData {
  KeyField?: string; // 행의 고유 ID
  [key: string]: any;
}

/** 상단 버튼(sys_headerctrl)이 달리는 cd_form. getInitForm 이 이 키로 내려준다. */
const HEADER_FORM = "grid2";

const SN_COS_MAS_REG_005 = forwardRef(({ Menuprops }: Props, ref) => {
  //#region  variable
  const gridApiRef1 = useRef<any>(null);
  const gridApiRef2 = useRef<any>(null);
  const searchRef = useRef<any>(null);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { userInfo, config } = global();
  //#endregion

  //#region panel 사이즈 관련
  const cd_menu = Menuprops.cd_menu || "SN_COS_MAS_REG_005";
  const defaultSize = {
    panel1width: "49.5%",
    panel2width: "49.5%",
  };
  const [sizes, onResizeStop] = usePanelSizes(cd_menu, defaultSize); // SnResizable들의 크기
  //#endregion

  //#region  Components useState
  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns1, setColums1] = useState<any[]>([]);
  const [columns2, setColums2] = useState<any[]>([]);
  const [dataSource1, setDataSource1] = useState<any[]>([]);
  const [dataSource2, setDataSource2] = useState<any>();
  const [gridFocus, setGridFocus] = useState(false);
  /** 상단 버튼 메타. getInitForm 이 이미 {controls, data} 로 가공해 주므로 다시 감싸지 않는다. */
  const [headerInfo, setHeaderInfo] = useState<any>({ controls: [], data: {} });
  let addedarow = false;

  //#endregion

  // 프로젝트 사용유무(통제환경 MAS0002)에 따라 프로젝트코드(cd_spare)·프로젝트명(nm_pjt) 표시/숨김.
  //  통제환경은 로그인 시 적재된 config 를 쓴다. 설정을 바꾼 뒤에는 재로그인해야 반영된다.
  const applyProjectVisible = (cols: any[], useProject: boolean) =>
    (cols || []).map((c: any) =>
      ["cd_spare", "nm_pjt"].includes(c.cd_column)
        ? { ...c, fg_visible: useProject ? "Y" : "N" }
        : c
    );

  useEffect(() => {
    const fetchform = async () => {
      setDataSource1([]);
      setDataSource2([]);
      try {
        const rtn: any = await getInitFormData({});

        // MAS0002(Project 사용유무). 미설정이면 기본 표시(Y)
        const dcProject = config?.MAS0002?.dc_ctrl;
        const useProject =
          dcProject === undefined || dcProject === null
            ? true
            : dcProject === "Y";

        setFormControlData(rtn.formControlData); // 조회조건 항목 설정
        setFormData(rtn.formData); // 조회조건 초기값
        setColums1(applyProjectVisible(rtn.grid.grid1, useProject)); // H그리드(프로젝트 컬럼 통제환경 반영)
        setColums2(applyProjectVisible(rtn.grid.grid2, useProject)); // L그리드(프로젝트 컬럼 통제환경 반영)
        setHeaderInfo(rtn.header?.[HEADER_FORM] ?? { controls: [], data: {} });
      } catch (error) {}
    };
    fetchform();
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, []);

  useEffect(() => {
    gridApiRef1.current?.selectAndFocusRow(0);
    gridApiRef2.current?.selectAndFocusRow(0);
    setGridFocus(false);
  }, [gridFocus]);

  /**
   * 원가기간 시작/종료월. SnPeriodMonthBoxPop 이 YYYYMM 을 준다.
   * 8자리로 오는 환경이 있어도 앞 6자리만 쓰면 안전하다.
   */
  const getYm = () => {
    const form = searchRef.current?.getFormdata() ?? {};
    return {
      ym_start: String(form.dt_from ?? "").substring(0, 6),
      ym_end: String(form.dt_to ?? "").substring(0, 6),
    };
  };

  //#region  Button Event
  useImperativeHandle(ref, () => ({
    onSearch,
    onInsert,
    onDelete,
  }));

  //region onSearch
  const onSearch = async () => {
    try {
      const formData = searchRef.current.getFormdata();
      // 조회조건이 SnPeriodMonthBoxPop 이라 YYYYMM(6자리)이 온다.
      // slice(0,-2) 는 뒤 2자리를 떼어 '2026' 을 만들어 char(6) 컬럼과 하나도 안 맞았다.
      const { ym_start, ym_end } = getYm();
      const params = {
        id: "sn_cos_mas_reg_005_01",
        params: { ...formData, ym_start: ym_start, ym_end: ym_end },
      };

      setDataSource1([]);
      setDataSource2([]);
      const data: any = await fnGetDataSet(params);
      setDataSource1(data[0]);
      setDataSource2(data[1] || []);

      if (data[0] && data[0].length > 0) {
        await new Promise((resolve) => setTimeout(resolve, 50));

        setGridFocus(true);
      }

      gridApiRef1.current.resetUpdate();
      gridApiRef2.current.resetUpdate();
      addedarow = false;
    } catch (error: any) {}
  };

  //region onInsert
  const onInsert = async () => {
    try {
      if (addedarow) return;
      const newRow = {};
      gridApiRef1.current?.addRow(newRow);
      addedarow = true;
    } catch (error: any) {
      showDialog("error", error);
    }
  };
  //region onDelete
  const onDelete = async () => {
    // GridV2 의 getSelectedRows 는 노드가 아니라 행 데이터를 주고, 없으면 null 이다.
    const selectedRows = gridApiRef1.current?.getSelectedRows() ?? [];
    const insertedRows = gridApiRef1.current?.getChange()?.insert ?? [];

    // No row selected
    if (selectedRows.length === 0) return;

    const selectedData = selectedRows[0];

    // Check if selected row is a new row (inserted)
    const isNewRow = insertedRows.some(
      (row: any) => row.id === selectedData.id
    );
    if (!isNewRow) {
      const confirm = await showDialog("confirm", "삭제하시겠습니까?");
      if (!confirm) return;
    }

    gridApiRef2.current?.deleteAllRows();
    gridApiRef1.current?.deleteSelectedRows();
  };

  /** 상단 버튼(sys_headerctrl)의 cd_param 이 sender 로 넘어온다. */
  //#region BtnClik
  /**
   * 부문별 배부 — 버튼 하나로 1차(공통비) → 2차(간접비)를 순차 실행한다.
   * 1차가 실패하면 2차로 넘어가지 않는다(2차는 1차 결과가 있어야 함).
   */
  const onBtnEtcClick = async (sender: string) => {
    // 부문별 배부 버튼(cd_param='dist')만 전체 배부로 처리한다.
    if (sender !== "dist") {
      return; // 이 화면이 모르는 버튼
    }

    const formData = searchRef.current.getFormdata();
    const { ym_start, ym_end } = getYm();
    const cd_firm = userInfo.cd_firm;
    const cd_biz = formData.cd_biz;

    if (!cd_biz || !ym_start || !ym_end) {
      await showDialog("alter", "사업장과 원가기간을 선택해주세요.");
      return;
    }

    // 조회/집계 호출이 throw 하면 화면에 아무것도 안 뜬다. 여기서 받아서 알린다.
    try {
      // 1차배부(공통비) — 실패 시 2차로 넘어가지 않는다
      const first = await callApi("SN_COS_MAS_REG_005_btnEvaluation", {
        cd_firm,
        cd_biz,
        ym_start,
        ym_end,
        no_hst: 1,
      });
      if (!first?.status) return; // 실패는 callApi 가 이미 오류 다이얼로그로 알린다

      // 1차 결과가 없으면 2차 SP 가 예외(1차배부 결과 없음)를 던지므로,
      // 여기서 먼저 확인해 안내메시지만 띄우고 2차는 건너뛴다.
      const cnt1: any = await fnGetDataSet({
        id: "sn_cos_mas_reg_005_05",
        params: { cd_firm, cd_biz, ym_end, no_hst: 1 },
      });
      if (Number(cnt1?.[0]?.[0]?.cnt ?? 0) === 0) {
        await onSearch();
        await showDialog(
          "alter",
          "배부된 내역이 없습니다. 요소비용집계로 요소금액이 생성되었는지 확인해주세요."
        );
        return;
      }

      // 2차배부(간접비) — 1차 결과 기반
      const second = await callApi("SN_COS_MAS_REG_005_btnEvaluation", {
        cd_firm,
        cd_biz,
        ym_start,
        ym_end,
        no_hst: 2,
      });
      if (!second?.status) return;

      // 집계 후 재조회
      await onSearch();

      // SP 는 원천이 없으면 0건을 넣고도 성공하므로, 결과가 비면 여기서 알린다(2차 기준).
      const rows: any = await fnGetDataSet({
        id: "sn_cos_mas_reg_005_05",
        params: { cd_firm, cd_biz, ym_end, no_hst: 2 },
      });

      if (Number(rows?.[0]?.[0]?.cnt ?? 0) === 0) {
        await showDialog(
          "alter",
          "배부된 내역이 없습니다. 요소비용집계·1차배부 결과와 2차 배부기준을 확인해주세요."
        );
      }
    } catch (error: any) {
      await showDialog(
        "error",
        "부문별 배부 처리 중 오류가 발생했습니다.\n" + (error?.message ?? "")
      );
    }
  };

  const onSearchValueChanged = (event: ValueChangedEvent) => {
    if (event.source !== "popup") return;
    if (event.colId !== "dt_from" && event.colId !== "dt_to") return;

    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      searchTimerRef.current = null;
      onSearch();
    }, 0);
  };

  //#region  UI
  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
          onValueChanged={onSearchValueChanged}
        ></SearchPanel>
      </div>
      {/* 부문별 배부 버튼은 sys_headerctrl 메타로 2차배부(우측) 그리드 상단에 붙는다 */}
      <div className="flex h-full w-full">
        <SnResizable
          direction={"width"}
          seq={1}
          cd_menu={cd_menu}
          defaultSize={defaultSize}
          size={sizes.panel1width}
          onResizeStop={onResizeStop}
          enable={{ right: true }}
          minWidth={130}
        >
          <SnGrid
            ref={gridApiRef1} // Grid Name
            columnConfig={columns1} // 컬럼 설정 전달
            rowData={dataSource1} // DataSource
            etcProps={{
              rowSelection: "single", // 행 선택 모드
              isEditable: false, // 수정 가능 여부
              title: " 1차배부(공통비배부) ", // 그리드 타이틀(좌우 여백: nbsp)
            }}
          />
        </SnResizable>
        <SnResizable
          direction={"width"}
          seq={2}
          cd_menu={cd_menu}
          defaultSize={defaultSize}
          size={sizes.panel2width}
          onResizeStop={onResizeStop}
          enable={{ right: true }}
          minWidth={130}
        >
          <SnGrid
            ref={gridApiRef2} // Grid Name
            columnConfig={columns2} // 컬럼 설정 전달
            rowData={dataSource2} // DataSource
            etcProps={{
              rowSelection: "single",
              isEditable: false, // 수정 가능 여부
              title: " 2차배부(간접비배부) ", // 그리드 타이틀(좌우 여백: nbsp)
            }}
            events={{ onBtnEtcClick }} // 부문별 배부 (sys_headerctrl)
            headerInfo={headerInfo}
          />
        </SnResizable>
      </div>
    </div>
  );
  //#endregion
});

export default SN_COS_MAS_REG_005;
